import React from "react";
import BaseModal from "./BaseModal";

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Privacy Policy"
      lastUpdated="December 2025"
      allowBackdropClose={true}
      showCloseButton={true}
      maxWidth="max-w-2xl"
      maxHeight="max-h-[85vh]"
      footerButtonText="Close"
      onFooterButtonClick={onClose}
    >
      <div className="space-y-6 text-gray-700 text-sm">
        {/* Item 1 */}

        <ol className="space-y-4 counter-reset pl-5">
          <li className="counter-item list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
            <h3 className="font-semibold text-gray-900">Overview</h3>
            <p>
              We respect your privacy and are committed to protecting your
              personal information. This Privacy Policy explains how we collect,
              use, disclose, and store data when you interact with our
              application (“Services”). The Services are not designed or
              intended for submitting, storing, or processing protected health
              information (PHI) or any medical records. By using the Services,
              you consent to the practices described in this Privacy Policy.
            </p>
          </li>

          <li className="counter-item list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
            {/* Item 2 */}
            <h3 className="font-semibold text-gray-900">
              Information We Collect
            </h3>

            <ol className="ml-6 mt-2 space-y-2">
              <li className="counter-subitem  list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900 ">
                {/* Item 2.1 */}
                <strong>Information You Provide</strong>
                <ul className="ml-6 mt-2 list-disc space-y-1">
                  <li>
                    Basic account information, such as your name, email address,
                    and login credentials.
                  </li>
                  <li>
                    Messages, prompts, support requests and interactions you
                    submit through the application.
                  </li>
                  <li>
                    We do not intentionally collect sensitive personal
                    information or PHI, and we request that you do not submit
                    such information.
                  </li>
                </ul>
              </li>

              {/* Item 2.2 */}
              <li className="counter-subitem list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
                <strong>Automatically Collected Information</strong>
                <ul className="ml-6 mt-2 list-disc space-y-1">
                  <li>
                    Login activity and usage metrics, such as last login time,
                    login count, and timestamps.
                  </li>
                  <li>
                    Device and technical information, including IP address,
                    error logs, and interaction data used for performance
                    monitoring.
                  </li>
                </ul>
              </li>
            </ol>
          </li>

          {/* Item 3 */}
          <li className="counter-item list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
            <h3 className="font-semibold text-gray-900">
              How We Use Your Information
            </h3>
            We use your information for the following purposes:
            {/* un ordered bullet point list*/}
            <ol className="list-disc space-y-2 ml-6">
              <li>
                Service delivery: To operate, maintain, and provide the features
                of our AI assistant.
              </li>
              <li>
                Personalization: To maintain session continuity and support
                contextual or personalized interactions.
              </li>
              <li>
                Analytics and improvement: To analyze usage patterns and system
                performance for debugging, optimization, and product
                development.
              </li>
              <li>
                Security and reliability: To detect misuse, maintain system
                integrity, and ensure accurate functionality.
              </li>
              <li>
                Compliance: To satisfy legal or regulatory obligations, where
                applicable.
              </li>
            </ol>
            {/* not a list item*/}
            <p className="pt-3">
              We do not use your data for advertising or sell your personal
              information.
            </p>
          </li>
          {/* item 4 */}
          <li className="counter-item list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
            <h3 className="font-semibold text-gray-900">
              Data Processing by Third Parties
            </h3>
            <p>
              User queries may be transmitted to trusted third-party AI or cloud
              service providers to generate responses or maintain system
              functionality. We do not send personally identifiable information
              such as your name or email address to these processing services.
              Audio and speech data are processed locally in your browser unless
              you choose to submit transcribed text as part of your message. All
              third parties are required to meet appropriate data protection
              standards.
            </p>
          </li>

          {/* Item 5 */}
          <li className="counter-item list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
            <h3 className="font-semibold text-gray-900">
              Data Storage and Retention
            </h3>
            <p>
              Data such as user messages, session details, and interaction logs
              may be stored in secure, managed cloud databases. Stored records
              may include timestamps, user messages, session identifiers,
              feedback and ratings, error logs and performance metrics. Access
              to this data is restricted to authorized personnel who require it
              for support, quality assurance, debugging, or product improvement.
              We retain data only as long as necessary to fulfill the purposes
              described in this policy or as required by law. You may request
              deletion of your data as described below.
            </p>
          </li>

          {/* Item 6 */}
          <li className="counter-item list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
            <h3 className="font-semibold text-gray-900">Security</h3>
            <p>
              We maintain reasonable and appropriate administrative, technical,
              and physical safeguards intended to protect your information
              against unauthorized access, loss, disclosure, or misuse. However,
              no system can guarantee absolute security.
            </p>
          </li>

          {/* item 7 */}
          <li className="counter-item list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
            <h3 className="font-semibold text-gray-900">
              Your Choices and Rights
            </h3>
            <p>
              You may request: deletion of your session data or account data;
              stop using the Services, at which point we will stop collecting
              new information; or, request information about how your data has
              been used. To make a request, please contact us using the
              information provided below.
            </p>
          </li>

          {/* Item 8 */}
          <li className="counter-item list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
            <h3 className="font-semibold text-gray-900">
              International Data Transfers
            </h3>
            <p>
              If you access the Services from outside the United States, your
              information may be transferred to and processed in the United
              States or other jurisdictions with different data protection laws.
              By using the Services, you consent to such transfers.
            </p>
          </li>

          {/* item 9 */}
          <li className="counter-item list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
            <h3 className="font-semibold text-gray-900">
              Changes to This Policy
            </h3>
            <p>
              We may update this Privacy Policy from time to time. Any material
              changes will be posted with an updated effective date. Continued
              use of the Services indicates acceptance of the revised policy.
            </p>
          </li>

          {/* item 10 */}
          <li className="counter-item list-decimal [&::marker]:font-semibold [&::marker]:text-gray-900">
            <h3 className="font-semibold text-gray-900">Contact Us</h3>
            <p>
              For questions, requests, or concerns about this Privacy Policy,
              please contact us at:{" "}
              <strong>customerservice@meditrina-inc.com</strong>
            </p>
          </li>
        </ol>
      </div>
    </BaseModal>
  );
};

export default PrivacyPolicyModal;
